import { WebsocketEvents } from '@websockets';

import { useClaimStore } from '~/store/claims';
import { useOrdersStore } from '~/store/orders';
import { useToastStore } from '~/store/toast';
import { useUserStore } from '~/store/user';

type NotificationType = 'info' | 'success' | 'warning' | 'error';

const ORDER_EVENTS = [WebsocketEvents.Order.Created, WebsocketEvents.Product.SyncCompleted];

const CLAIM_EVENTS = [
  WebsocketEvents.Message.Created,
  WebsocketEvents.Claim.Created,
  WebsocketEvents.Claim.Updated,
  WebsocketEvents.Claim.StateUpdated,
  WebsocketEvents.Claim.Assigned,
  WebsocketEvents.Claim.AutomationStatus,
  WebsocketEvents.Claim.AutomationCreated,
  WebsocketEvents.Order.Reship.Created,
  WebsocketEvents.OrderItem.Created,
];

export default defineNuxtPlugin(async () => {
  const orders = useOrdersStore();
  const user = useUserStore();
  const realtime = useRealtime();
  const claims = useClaimStore();
  const toasts = useToastStore();

  const { account } = storeToRefs(user);

  let subscribedChannel: string | null = null;

  function createNotification(event: string, message: { data?: any }) {
    switch (event) {
      case WebsocketEvents.Message.Created:
        if (message.data.senderType !== 'customer') return;

        return {
          // having an id here should prevent duplicate notifications
          id: (message.data as any).claimId,
          message: `New message on claim #${message.data.claimId}.`,
          to: `/claims/view/${message.data.orderId}`,
          type: 'info' as NotificationType,
        };
      case WebsocketEvents.Claim.Created:
        return {
          id: (message.data as any).id,
          message: `New claim #${message.data.id} has been opened by ${message.data.authorName}.`,
          to: `/claims/view/${message.data.orderId}`,
          type: 'info' as NotificationType,
        };
      default:
        return null;
    }
  }

  // Signing in as a different account in the same tab must not leave the old channel
  // attached, or the tab receives both accounts' events.
  function subscribe(value: string) {
    if (subscribedChannel === value) return;

    if (subscribedChannel) {
      for (const event of [...ORDER_EVENTS, ...CLAIM_EVENTS]) realtime.unsubscribe(subscribedChannel, event);
    }

    subscribedChannel = value;

    for (const event of ORDER_EVENTS) {
      realtime.subscribe(value, event, async message => {
        orders.$bus.emit(event, message.data);
      });
    }

    for (const event of CLAIM_EVENTS) {
      realtime.subscribe(value, event, async message => {
        const notification = createNotification(event, message);

        if (notification) {
          // toasts.addToast(notification); // Legacy toast notifications disabled - using new notifications feature
        }

        claims.$bus.emit(event, message.data);
      });
    }
  }

  // `init.ts` sorts before this plugin and awaits postLogin(), so on a reload `account` is
  // already set and a non-immediate watch would never fire (ENG-6014). Nothing subscribes
  // while `account` is falsy, so no Ably client is constructed on /login/* (ENG-5882).
  watch(
    account,
    value => {
      if (value) subscribe(value);
    },
    { immediate: true },
  );
});
